import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import { Loader } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-roboto font-medium transition-all duration-200 focus-visible:outline-none disabled:pointer-events-none",
  {
    variants: {
      variant: {
        // Default: Light background with dark text (main CTA) - using design system variables
        default:
          "bg-text-body-primary text-primary-foreground hover:shadow-sm focus-visible:ring-3 focus-visible:ring-muted-foreground/50 active:opacity-60 disabled:opacity-50 shadow-sm",

        // Secondary: Dark background with light text - using design system variables
        secondary:
          "bg-secondary text-secondary-foreground hover:shadow-sm focus-visible:ring-3 focus-visible:ring-muted-foreground/50 active:opacity-60 disabled:opacity-50 shadow-sm",

        // Ghost: Transparent with subtle hover - using design system variables
        ghost:
          "bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-3 focus-visible:ring-muted-foreground/50 active:opacity-60 disabled:opacity-50",

        // Link: Text with underline on hover - using design system variables
        link: "bg-transparent text-text-body-primary hover:underline focus-visible:ring-3 focus-visible:ring-muted-foreground/50 active:opacity-60 disabled:opacity-50",

        // Outline: Border with glass background - using design system variables
        outline:
          "bg-white/5 border-2 border-text-body-primary text-foreground hover:bg-white/[0.08] hover:border-white/[0.04] focus-visible:ring-3 focus-visible:ring-muted-foreground/50 focus-visible:border-ring active:opacity-60 disabled:opacity-50 shadow-sm",

        // Destructive: Red/pink theme - using design system variables
        destructive:
          "bg-destructive/60 text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-3 focus-visible:ring-destructive/40 active:opacity-60 disabled:opacity-50 shadow-sm",

        // Glass: Separate gradient border and solid background with hover animation
        glass:
          "hover-animated-border backdrop-blur-md text-[rgb(248,240,200)] !justify-center hover-glow-gold",
      },
      size: {
        sm: "rounded-md px-4 py-2 min-h-8 text-xs leading-4",

        default: "rounded-md px-4 py-2 min-h-9 text-sm leading-5",

        lg: "rounded-md px-10 py-3 min-h-12 text-lg leading-6 ",

        icon: "rounded-md p-2 w-9 h-9",

        rectangle: "rounded-md px-10 py-3 min-h-12 text-base leading-6",
      },
      loading: {
        true: "cursor-not-allowed opacity-50",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      loading: false,
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      loading = false,
      disabled,
      asChild = false,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    const isDisabled = disabled || loading;

    // Apply accent color for glass variant
    const glassStyle =
      variant === "glass"
        ? ({
            "--accent-colour": "255, 255, 160",
            ...props.style,
          } as React.CSSProperties)
        : props.style;

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, loading }), className)}
        ref={ref}
        disabled={isDisabled}
        {...props}
        style={glassStyle}
      >
        {loading ? (
          <>
            <Loader className="h-4 w-4 animate-spin" />
            {children}
          </>
        ) : (
          <>
            {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
          </>
        )}
      </Comp>
    );
  },
);

export { Button, buttonVariants };
